<template>
    <ui-container class="py-8 space-y-8" :key="routePlanKey">
        <!-- Header. -->
        <ui-header :title="`Route Plan ${item.formatted_date}`" :subtitle="' '">
            <template #actions>
                <ui-button-new icon="fas fa-pencil" @click="lockAndEdit"
                               v-if="!item.editing || (item.editingByReference === user.user.id)">Edit
                </ui-button-new>
                <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal"
                               v-if="!item.editing || (item.editingByReference === user.user.id)">Delete
                </ui-button-new>
                <div v-if="user.user && item.editing && (item.editingByReference !== user.user.id)"
                     class="text-red-600 text-xs mt-2 w-72">Route Plan is currently being edited by {{ item.editingBy }}
                    (since
                    {{ item.editingSince }})
                </div>
            </template>
        </ui-header>
        <ui-data-grid>
            <ui-data-grid-item wide label="Route Plan ID" :value="'#'+item.id"></ui-data-grid-item>
            <ui-data-grid-item label="Vehicle Registration" raw
                               :value="highlight(item.vehicleRegistration)"></ui-data-grid-item>
            <ui-data-grid-item label="Driver" raw :value="highlight(item.driver)"></ui-data-grid-item>
        </ui-data-grid>
        <div class="space-y-0">
            <label class="py-0">Jobs</label>
            <div class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold"
                 v-if="!singleColumn">
                <div class="w-6 flex-shrink-0"></div>
                <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Address</div>
                <div class="flex-1 border-r border-gray-200 px-2 py-1">Contact</div>
                <div class="flex-1 border-r border-gray-200 px-2 py-1">Materials</div>
                <div class="w-6 flex-shrink-0 ml-2"></div>
            </div>
            <template v-for="job in item.jobs">
                <div class="flex border-collapse border-b border-gray-200"
                     :class="backgroundColor(job)+(singleColumn?' border-t':'')">
                    <div class="w-6 flex-shrink-0"></div>
                    <template v-if="!singleColumn">
                        <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                            <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                 v-html="job.address ? highlight(job.address) : '&nbsp;'"></div>
                        </div>
                        <div class="flex-1 border-r border-gray-200 px-0 py-0">
                            <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                 v-html="job.contact ? highlight(job.contact) : '&nbsp;'"></div>
                        </div>
                        <div class="flex-1 border-r border-gray-200 px-0 py-0">
                            <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                 v-html="job.materials ? highlight(job.materials) : '&nbsp;'"></div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                            <div class="w-full px-0 py-0">
                                <div
                                    class="border-gray-200 bg-gray-50 border-b text-xs text-gray-500 font-semibold py-1 px-2">
                                    Address
                                </div>
                                <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                     v-html="job.address ? highlight(job.address) : '&nbsp;'"></div>
                            </div>
                            <div class="w-full px-0 py-0">
                                <div
                                    class="border-gray-200 bg-gray-50 border-t border-b text-xs text-gray-500 font-semibold py-1 px-2">
                                    Contact
                                </div>
                                <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                     v-html="job.contact ? highlight(job.contact) : '&nbsp;'"></div>
                            </div>
                            <div class="w-full px-0 py-0">
                                <div
                                    class="border-gray-200 bg-gray-50 border-t border-b text-xs text-gray-500 font-semibold py-1 px-2">
                                    Materials
                                </div>
                                <div class="text-sm px-2 py-1" style="white-space: pre-wrap"
                                     v-html="job.materials ? highlight(job.materials) : '&nbsp;'"></div>
                            </div>
                        </div>
                    </template>
                    <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
                        <ui-dropdown menu-class="p-3 w-56">
                            <template #default="{ toggle }">
                                <button @click="toggle"
                                        class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                    <i class="fa fa-ellipsis-h fa-fw"></i>
                                </button>
                            </template>
                            <template #menu="{ close }">
                                <template v-if="!item.editing || (item.editingByReference === user.user.id)">
                                    <ui-dropdown-item icon="fas fa-undo" @click="resetJob(job);close();"
                                                      v-if="item.isInPast() && (job.status!=='Awaiting')">Un-Mark Job
                                    </ui-dropdown-item>
                                    <ui-dropdown-item icon="fas fa-check" success @click="completeJob(job);close();"
                                                      v-if="item.isInPast() && (job.status==='Awaiting')">Mark as
                                        Complete
                                    </ui-dropdown-item>
                                    <ui-dropdown-item icon="fas fa-times" danger @click="failJob(job);close();"
                                                      v-if="item.isInPast() && (job.status==='Awaiting')">Unable to
                                        Complete
                                    </ui-dropdown-item>
                                    <hr class="m-1.5" v-if="item.isInPast()"/>
                                </template>
                                <ui-dropdown-item icon="fas fa-copy" @click="copyJob(job);close();">Copy
                                </ui-dropdown-item>
                                <hr v-if="clipboard.length" class="m-1.5"/>
                                <ui-dropdown-item icon="fas fa-clipboard" @click="clearClipboard();close();"
                                                  v-if="clipboard.length">
                                    {{ 'Clear Clipboard (' + clipboard.length + ' jobs)' }}
                                </ui-dropdown-item>
                            </template>
                        </ui-dropdown>
                    </div>
                </div>
                <div class="w-full h-4" v-if="singleColumn"></div>

            </template>
        </div>

        <send-log :send-log="combineSendLogs" @revoke="revoke" @resend="resend"></send-log>
        <ui-data-grid-item label="Quick action" wide>
            <div class="flex flex-col md:flex-row flex-wrap -m-2">
                <ui-button-new class="m-2" icon="fas fa-route" @click="duplicate" success>Duplicate
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-check" @click="markAllComplete" success
                               v-if="(!item.editing || (item.editingByReference === user.user.id)) && item.isInPast()">
                    Mark all as Complete
                </ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-file-pdf"><a :href="item.getDownloadUrl()" tabindex="-1">Download
                    PDF</a></ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-print" @click="print">Print PDF</ui-button-new>
                <ui-button-new class="m-2" icon="fas fa-share" @click="openSendModal" extra>Send
                </ui-button-new>
            </div>
        </ui-data-grid-item>
        <ui-data-grid>
            <ui-data-grid-item label="Created By">{{ item.createdBy }} on {{ item.createdOn }}</ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="item.lastUpdatedBy">{{ item.lastUpdatedBy }} on {{
                    item.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>
</template>

<script>
import RoutePlansJobItem from "./RoutePlansJobItem.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiContainer from "../../ui/UiContainer.vue";
import {RoutePlan} from '@models'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import {cloneDeep, isNull} from "lodash";
import User from "../../../services/User";
import SendLog from "../../send-log/SendLog.vue";
import moment from "moment/moment";

export default {
    components: {
        SendLog,
        UiDataGridItem,
        UiDataGrid,
        UiButtonNew, UiDropdown, UiDropdownItem, UiContainer, AutoresizableTextarea, RoutePlansJobItem
    },
    props: {
        item: Object,
        edit: Function,
        removeItem: Function,
        addItem: Function,
        search: String,
        singleColumn: false,
    },
    computed: {
        combineSendLogs() {
            return this.item.sendLog.map((a) => {
                return a;
            }).sort((a, b) => moment(b.requested.at).unix() - moment(a.requested.at).unix())
        }
    },
    data() {
        const clipboardData = localStorage.getItem('route-plan-clipboard');
        return {
            clipboard: clipboardData ? JSON.parse(clipboardData) : [],
            user: User,
            routePlanKey: 0
        }
    },
    methods: {
        async openSendModal() {
            this.$modals.open("route-plan-send", {
                title: `Send Route Plan`,
                button: "Send Route Plan",
                routePlan: this.item,
                handle: async (item, modal) => {
                    modal.close();
                },
            });
        },
        revoke(i) {
            this.item.revoke(i.id);
        },
        resend(i) {
            this.item.resend(i.id);
        },
        async lockAndEdit() {
            try {
                await this.item.acquireEditLock(User.connectionId)
                this.edit(this.item)
            } catch (e) {
                this.routePlanKey++;
            }
        },
        highlight(string) {
            const query = this.search.toLowerCase().split(/[^A-Za-z0-9.-]+/).filter((item) => {
                return item.trim() !== ''
            })
            let reg = new RegExp(query.join('|'), 'gi');
            return string.replace(reg, "<span class='bg-blue-200'>$&</span>");
        },
        isNull,
        openDeleteModal() {
            const title = 'Are you sure you want to remove route plan ' + this.item.formatted_date + '?'
            const description = 'You are removing route plan #' + this.item.id + '. Are you sure you want to do this?'
            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.item.delete()
                this.removeItem(this.item)

            });
        },
        async duplicate() {
            const routePlan = await RoutePlan.create(this.item.getFormAttributes());

            this.addItem(routePlan, 0, (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            });
            this.edit(routePlan);
        },
        print() {
            this.$emit('print', this.item.getPrintableUrl())
        },
        markAllComplete() {
            this.item.completeAllJobs();
        },
        completeJob(job) {
            this.item.completeJob(job);
        },
        failJob(job) {
            this.item.failJob(job);
        },
        resetJob(job) {
            this.item.resetJob(job);
        },
        copyJob(job) {
            this.clipboard.push(cloneDeep(job));
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
        },
        clearClipboard() {
            this.clipboard = [];
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
        },
        backgroundColor(job) {
            if (job.status === 'Complete') {
                return 'bg-green-50';
            }
            if (job.status === 'Unable to Complete') {
                return 'bg-red-50';
            }
            return 'bg-white';
        }
    },
    mounted() {
        this.user = User;
    }
}
</script>
'